import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { ValidateResult } from '../../../Vendor/react-hook-form'
import { RulesLanguage } from '../../../../i18n'
import { IsStringEmpty, isStringNotEmpty, isVarNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { EMAIL_REGEX } from '../../../NonReact/constants/Regexes'
import { EMPTY_STRING, SPACE } from '../Constants/Separators'
import { Protocols } from '../../../NonReact/constants/Protocols'

type Rule<T extends [...unknown[]]> = (
    language: CLanguageHandler,
    languageKey?: string,
    ...args: T
) => (value: string) => ValidateResult

export type RulesType = {
    isNumber: Rule<[]>
    isInteger: Rule<[]>
    isPositiveNumber: Rule<[]>
    isUnderMaxSafeInteger: Rule<[]>
    isUnderLimit: Rule<[number, boolean]>
    isInRange: Rule<[number, number]>
    isEmailValid: Rule<[]>
    mustMatch: Rule<[string]>
    minLength: Rule<[Definable<number>]>
    isValidUrl: Rule<[]>
    isEmpty: Rule<[]>
    required: Rule<[]>
    pattern: Rule<[string]>
    maxLength: Rule<[Definable<number>]>
    maxWordLength: Rule<[Definable<number>]>
}

const loadLanguage = (language: CLanguageHandler, languageKey?: string): void => {
    if (IsStringEmpty(languageKey)) {
        language.loadBundle(RulesLanguage.NAME)
    }
}

export class RulesUtils {
    private constructor() {
        // Utility class, prevent instantiation.
    }

    public static isNumber(value: string): boolean {
        return isStringNotEmpty(value) && !isNaN(Number(value))
    }

    public static isInteger(value: string): boolean {
        return RulesUtils.isNumber(value) && Number.isInteger(Number(value))
    }

    public static isPositiveNumber(value: string): boolean {
        return RulesUtils.isNumber(value) && Number(value) >= 0
    }

    public static isUnderMaxSafeInteger(value: string): boolean {
        return RulesUtils.isNumber(value) && Number(value) <= Number.MAX_SAFE_INTEGER
    }

    public static isUnderLimit(value: string, limit: number): boolean {
        return RulesUtils.isNumber(value) && Number(value) <= limit
    }

    public static isInRange(value: string, min: number, max: number): boolean {
        return RulesUtils.isNumber(value) && Number(value) >= min && Number(value) <= max
    }

    public static isEmailValid(email: string): boolean {
        return !IsStringEmpty(email) && !!email?.match(EMAIL_REGEX)
    }

    public static isValidUrl(value: string): boolean {
        if (IsStringEmpty(value)) return true

        const trimmedValue: string = value.trim()

        if (!/^(https?):\/\//i.test(trimmedValue)) return false

        try {
            const url: URL = new URL(trimmedValue)

            const isHttp: boolean = url.protocol === Protocols.HTTP || url.protocol === Protocols.HTTPS
            const hasHost: boolean = url.hostname.length > 0
            const nonNullOrigin: boolean = url.origin !== 'null'

            return isHttp && hasHost && nonNullOrigin
        } catch {
            return false
        }
    }

    public static mustMatch(value: string, matchValue: string): boolean {
        return value === matchValue
    }

    public static minLength(value: number, minLength: Definable<number>): boolean {
        return value >= (minLength ?? 0)
    }

    public static maxLength(value: number, maxLength: Definable<number>): boolean {
        return value <= (maxLength ?? 0)
    }

    public static maxWordLength(value: string, maxWordLength: Definable<number>): boolean {
        if (isStringNotEmpty(value)) {
            const words: string[] = value.split(SPACE)
            return !words.some((word: string): boolean => word.length > (maxWordLength ?? 0))
        }
        return true
    }

    public static isEmpty(value: string): boolean {
        return IsStringEmpty(value)
    }

    public static pattern(value: string, pattern: string): boolean {
        return isStringNotEmpty(value) && !!value.match(pattern)
    }
}

export const Rules: RulesType = {
    /**
     * Rule to validate a string is a number.
     */
    isNumber:
        (language: CLanguageHandler) =>
        (value: string): ValidateResult => {
            loadLanguage(language)

            if (isStringNotEmpty(value) && !RulesUtils.isNumber(value)) {
                return language.get(RulesLanguage.Rules_Validation_MustBeANumber)
            }

            return undefined
        },

    /**
     * Rule to validate a string is an integer.
     */
    isInteger:
        (language: CLanguageHandler) =>
        (value: string): ValidateResult => {
            loadLanguage(language)

            const isNumberRes: ValidateResult = Rules.isNumber(language)(value)

            if (isVarNotEmpty(isNumberRes)) {
                return isNumberRes
            }

            if (isStringNotEmpty(value) && !RulesUtils.isInteger(value)) {
                return language.get(RulesLanguage.Rules_Validation_MustBeAnInteger)
            }

            return undefined
        },

    /**
     * Rule to validate a string is a positive number.
     */
    isPositiveNumber:
        (language: CLanguageHandler) =>
        (value: string): ValidateResult => {
            loadLanguage(language)

            const isNumberRes: ValidateResult = Rules.isNumber(language)(value)

            if (isVarNotEmpty(isNumberRes)) {
                return isNumberRes
            }

            if (isStringNotEmpty(value) && !RulesUtils.isPositiveNumber(value)) {
                return language.get(RulesLanguage.Rules_Validation_MustBeGreaterThanZero)
            }

            return undefined
        },

    /**
     * Rules to validate a string is a number less than or equal to MAX_SAFE_INTEGER.
     */
    isUnderMaxSafeInteger:
        (language: CLanguageHandler) =>
        (value: string): ValidateResult => {
            loadLanguage(language)

            const isNumberRes: ValidateResult = Rules.isNumber(language)(value)

            if (isVarNotEmpty(isNumberRes)) {
                return isNumberRes
            }

            if (isStringNotEmpty(value) && !RulesUtils.isUnderMaxSafeInteger(value)) {
                return language.get(RulesLanguage.Rules_Validation_MustBeLessThanMaxSafeInteger)
            }

            return undefined
        },

    /**
     * Rule to validate a string is a number less than or equal to a given limit.
     */
    isUnderLimit:
        (language: CLanguageHandler, languageKey?: string, ...[limit, isRequired]: [number, boolean]) =>
        (value: string): ValidateResult => {
            loadLanguage(language, languageKey)

            const isNumberRes: ValidateResult = Rules.isNumber(language)(value)

            if (isVarNotEmpty(isNumberRes)) {
                return isNumberRes
            }

            const isOverLimit: boolean =
                isVarNotEmpty(limit) && isStringNotEmpty(value) && !RulesUtils.isUnderLimit(value, limit)

            if (isOverLimit || (isRequired && IsStringEmpty(value))) {
                return language.getInterpolated(
                    languageKey ?? RulesLanguage.Rules_Validation_MustBeLessThanLimit,
                    limit === Number.MAX_SAFE_INTEGER ? language.get(RulesLanguage.Rules_Numbers_MaxSafeInteger) : limit
                )
            }

            return undefined
        },

    /**
     * Rule to validate a string is a number within a given inclusive range [min, max].
     */
    isInRange:
        (language: CLanguageHandler, languageKey?: string, ...[min, max]: [number, number]) =>
        (value: string): ValidateResult => {
            loadLanguage(language, languageKey)

            const isNumberRes: ValidateResult = Rules.isNumber(language)(value)

            if (isVarNotEmpty(isNumberRes)) {
                return isNumberRes
            }

            if (isStringNotEmpty(value) && !RulesUtils.isInRange(value, min, max)) {
                return language.getInterpolated(languageKey ?? RulesLanguage.Rules_Validation_MustBeInRange, min, max)
            }

            return undefined
        },

    /**
     * Rule to validate an email is valid.
     */
    isEmailValid:
        (language: CLanguageHandler, languageKey?: string) =>
        (email: string): ValidateResult => {
            const lang: CLanguageHandler = language ?? CLanguageHandler.getInstance()

            loadLanguage(lang, languageKey)

            if (!IsStringEmpty(email) && !RulesUtils.isEmailValid(email)) {
                return lang.get(languageKey ?? RulesLanguage.Rules_Validation_EmailMustBeValid)
            }

            return undefined
        },

    /**
     * Rule to validate a string matches a given value.
     *
     * @param language - The language handler.
     * @param languageKey - The language key.
     * @param matchValue - The value to match.
     */
    mustMatch:
        (language: CLanguageHandler, languageKey?: string, ...[matchValue]: [string]) =>
        (value: string): ValidateResult => {
            loadLanguage(language, languageKey)

            if (!RulesUtils.mustMatch(value, matchValue ?? EMPTY_STRING)) {
                return language.get(languageKey ?? RulesLanguage.Rules_Validation_MustMatch)
            }

            return undefined
        },

    /**
     * Rule to validate a string is greater than a given length.
     *
     * @param language - The language handler.
     * @param languageKey - The language key.
     * @param minLength - The minimum length.
     */
    minLength:
        (language: CLanguageHandler, languageKey?: string, ...[minLength]: [Definable<number>]) =>
        (value: string): ValidateResult => {
            loadLanguage(language, languageKey)

            if (isStringNotEmpty(value) && !RulesUtils.minLength(value.length, minLength ?? 0)) {
                return language.getInterpolated(languageKey ?? RulesLanguage.Rules_Validation_MinLength, minLength)
            }

            return undefined
        },

    isValidUrl:
        (language: CLanguageHandler, languageKey?: string) =>
        (value: string): ValidateResult => {
            loadLanguage(language, languageKey)

            if (IsStringEmpty(value)) return undefined

            return RulesUtils.isValidUrl(value)
                ? undefined
                : language.get(languageKey ?? RulesLanguage.Rules_Validation_ValidUrl)
        },

    isEmpty:
        (language: CLanguageHandler, languageKey?: string) =>
        (value: string): ValidateResult => {
            loadLanguage(language, languageKey)

            if (IsStringEmpty(value)) {
                return language.get(languageKey ?? RulesLanguage.Rules_Validate_Empty)
            }

            return undefined
        },

    required:
        (language: CLanguageHandler, languageKey?: string) =>
        (value: string): ValidateResult => {
            loadLanguage(language, languageKey)

            if (IsStringEmpty(value)) {
                return language.get(languageKey ?? RulesLanguage.Rules_Validate_Empty)
            }

            return undefined
        },

    pattern:
        (language: CLanguageHandler, languageKey?: string, pattern?: string) =>
        (value: string): ValidateResult => {
            loadLanguage(language, languageKey)

            if (isStringNotEmpty(value) && !RulesUtils.pattern(value, pattern ?? EMPTY_STRING)) {
                return language.get(languageKey ?? RulesLanguage.Rules_Validation_IncorrectPattern)
            }

            return undefined
        },

    maxLength:
        (language: CLanguageHandler, languageKey?: string, maxLength?: Definable<number>) =>
        (value: string): ValidateResult => {
            loadLanguage(language, languageKey)

            if (isStringNotEmpty(value) && !RulesUtils.maxLength(value.length, maxLength ?? 0)) {
                return language.getInterpolated(languageKey ?? RulesLanguage.Rules_Validation_MaxLength, maxLength)
            }

            return undefined
        },

    maxWordLength:
        (language: CLanguageHandler, languageKey?: string, maxWordLength?: Definable<number>) =>
        (value: string): ValidateResult => {
            loadLanguage(language, languageKey)

            return isStringNotEmpty(value) && RulesUtils.maxWordLength(value, maxWordLength ?? 0)
                ? language.getInterpolated(languageKey ?? RulesLanguage.Rules_Validation_MaxWordLength, maxWordLength)
                : undefined

            return undefined
        }
}
